import templateConst from 'app/common/lib/templateConst';
import { INTEREST_LIST_FAVORITED } from 'app/packages/pinterest-constants';

const MAX_NAME_LENGTH = 32;
const truncateName = (name) =>
  name.length > MAX_NAME_LENGTH ? `${name.substring(0, MAX_NAME_LENGTH).trim()}...` : name;

export default ({ i18n, interpolateNamedTemplate }) => ({
  getLearnMoreUrl: (ad_match_reason) => {
    switch (ad_match_reason) {
      case PCONST.thrift.MatchReason.VISITOR_TARGETING:
        return templateConst.settings.HELP_PROMOTED_PINS_RETARGETED_PIN_URL;
      case PCONST.thrift.MatchReason.LOOKALIKE_TARGETING:
      case PCONST.thrift.MatchReason.CRM_TARGETING:
        return templateConst.settings.HELP_PROMOTED_PINS_LOOKALIKE_TARGETING_URL;
      default:
        return templateConst.settings.HELP_PROMOTED_PINS_URL;
    }
  },
  getRecommendationDescription: ({
    ad_match_reason,
    board,
    feedbackType,
    makeNavigateLink,
    pinner,
    promoter,
    recommendationReason,
    sourceInterest,
  }) => {
    switch (feedbackType) {
      case 'pfy':
        return i18n._('This Pin was inspired by your recent activity', 'Pin feedback reason');
      case 'pfyBoard':
        const { name: boardName, url: boardUrl } = recommendationReason.board;
        const boardLink = makeNavigateLink('navigation', boardUrl, boardName);
        return interpolateNamedTemplate(
          i18n._(
            'This Pin was inspired by your board {{ boardLink }}',
            'Pin feedback - inspired by your board',
          ),
          { boardLink },
        );
      case 'topic':
        const { name: topicName, url_name: urlName } = sourceInterest;

        const url = `/topics/${urlName}`;
        const formattedTopic = makeNavigateLink('topicUrl', url, topicName);

        return interpolateNamedTemplate(
          i18n._('This Pin was inspired by {{ formattedTopic }}', 'Pin feedback'),
          { formattedTopic },
        );

      case 'followed':
        const pinnerUrl = `/${pinner.username}`;
        const formattedBoard = makeNavigateLink('boardUrl', board.url, truncateName(board.name));
        const formattedPinner = makeNavigateLink(
          'pinnerUrl',
          pinnerUrl,
          truncateName(pinner.full_name),
        );

        const description = pinner.explicitly_followed_by_me
          ? interpolateNamedTemplate(
              i18n._(
                'This Pin is from {{ formattedPinner }}, a profile you follow',
                'Pin feedback',
              ),
              { formattedPinner },
            )
          : interpolateNamedTemplate(
              i18n._('This Pin is from {{ formattedBoard }}, a board you follow', 'Pin feedback'),
              { formattedBoard },
            );

        return description;
      case 'promoted':
        switch (ad_match_reason) {
          case PCONST.thrift.MatchReason.VISITOR_TARGETING:
          case PCONST.thrift.MatchReason.LOOKALIKE_TARGETING:
          case PCONST.thrift.MatchReason.CRM_TARGETING:
            return i18n._(
              'This ad was personalized for you using info from one of our partners',
              'Pin feedback',
            );
          default:
            const { full_name, username } = promoter;
            const url = username ? '/' + username + '/' : '/';
            const promoterLink = makeNavigateLink('promotedLink', url, full_name);

            // Removal of period punctuation in v2
            return interpolateNamedTemplate(
              i18n._(
                `{{ promoterLink }} paid to have this Pin show up where
            you’d be more likely to notice it`,
                'Pin feedback',
              ),
              { promoterLink },
            );
        }
      default:
        return null;
    }
  },
  getFeedbackType({
    board,
    feedback,
    viewParameter,
    pin: {
      promoter,
      is_downstream_promotion: isDownstreamPromotion,
      recommendation_reason: recommendationReason,
    },
    pinner,
  }) {
    const feedbackType = feedback && feedback.type;
    if (promoter && !isDownstreamPromotion) {
      return 'promoted';
    } else if (feedbackType) {
      return feedbackType;
    } else if (recommendationReason) {
      if (recommendationReason.reason === 'FOLLOWED_INTEREST') {
        return 'topic';
      } else if (recommendationReason.board) {
        return 'pfyBoard';
      } else {
        return 'pfy';
      }
    } else if ((board && board.followed_by_me) || (pinner && pinner.explicitly_followed_by_me)) {
      return 'followed';
    } else if (
      viewParameter === PCONST.thrift.ViewParameterType.PIN_REGULAR ||
      viewParameter === PCONST.thrift.ViewParameterType.PIN_PRODUCT ||
      viewParameter === PCONST.thrift.ViewParameterType.PIN_ARTICLE ||
      viewParameter === PCONST.thrift.ViewParameterType.PIN_RECIPE ||
      viewParameter === PCONST.thrift.ViewParameterType.PIN_OTHER ||
      viewParameter === PCONST.thrift.ViewParameterType.BOARD_IDEAS
    ) {
      return 'related';
      // In homefeed we always want to set the feedbackType
      // Moving forward we'll also be doing this for search & related pins
    } else if (
      // homefeed
      viewParameter === PCONST.thrift.ViewParameterType.FEED_HOME
    ) {
      return 'default';
    } else if (viewParameter === PCONST.thrift.ViewParameterType.NEWS_HUB_DETAIL) {
      return 'newsHub';
    } else {
      return null;
    }
  },
  getHidePinAction: ({
    feedbackData,
    feedbackType,
    pinId,
    recommendationReasonId,
    pinFeedbackType,
    complaintReason,
    throughId,
  }) => {
    const promoted = feedbackType === 'promoted';

    let action;
    switch (feedbackType) {
      case 'promoted':
        action = 'promotedComplaint';
        break;
      case 'search':
        action = 'unrelatedSearchPin';
        break;
      case 'related':
        action = 'relatedPinHide';
        break;
      case 'newsHub':
        action = 'newsHubPinHide';
        break;
      default:
        action = 'complaint';
        break;
    }

    let actionOptions;
    switch (feedbackType) {
      case 'search':
        actionOptions = {
          query: feedbackData.query,
          pin_id: pinId,
        };
        break;
      case 'related':
        actionOptions = {
          pin: pinId,
        };
        break;
      case 'newsHub':
        actionOptions = {
          pinId,
          newsId: feedbackData.newsId,
        };
        break;
      default:
        actionOptions = {
          complaint_reason:
            complaintReason || PCONST.thrift.RecommendationComplaintReason.OTHER_REASON,
          pin_id: pinId,
          ...(recommendationReasonId ? { recommendation_reason_id: recommendationReasonId } : {}),
          feedback_type: pinFeedbackType || PCONST.thrift.PinFeedbackTypes.BLOCK_SINGLE_PFY_PIN,
          promoted,
          ...(throughId ? { through_id: throughId } : {}),
        };
        break;
    }

    return {
      action,
      actionOptions,
    };
  },
  getUnfollowAction: ({ board, feedbackType, makeNavigateLink, pinner, sourceInterest }) => {
    if (feedbackType === 'topic') {
      if (sourceInterest) {
        const { id, name: topicName, url_name: urlName } = sourceInterest;
        const url = `/topics/${urlName}`;
        const formattedTopic = makeNavigateLink('topicUrl', url, topicName, true);

        return {
          action: 'unfollowTopic',
          actionOptions: {
            interest_id: id,
            interest_list: INTEREST_LIST_FAVORITED,
          },
          title: '',
          subTitle: interpolateNamedTemplate(
            i18n._(
              'We’ll show you less Pins from {{ formattedTopic }} in the future.',
              'Confirmation text after unfollowing a topic',
            ),
            { formattedTopic },
          ),
        };
      }
    } else if (feedbackType === 'followed') {
      const { id: boardId, name: boardName, url: boardUrl } = board || {};
      const {
        explicitly_followed_by_me: explicitlyFollowedByMe,
        full_name: pinnerName,
        username,
      } = pinner;
      const pinnerUrl = `/${username}`;

      if (board && pinner) {
        const formattedBoard = makeNavigateLink('boardUrl', boardUrl, boardName, true);
        const formattedPinner = makeNavigateLink('pinnerUrl', pinnerUrl, pinnerName, true);

        let subTitle = explicitlyFollowedByMe
          ? interpolateNamedTemplate(
              i18n._(
                'You’ve unfollowed {{ formattedPinner }}’s Board {{ formattedBoard }}. You can follow it again by searching for it.',
                'Confirmation text after unfollowing a board',
              ),
              { formattedPinner, formattedBoard },
            )
          : interpolateNamedTemplate(
              i18n._(
                'You’ve unfollowed {{ formattedBoard }}. You can follow it again by searching for it.',
                'Confirmation text after unfollowing a board',
              ),
              { formattedBoard },
            );

        subTitle = explicitlyFollowedByMe
          ? interpolateNamedTemplate(
              i18n._(
                'You unfollowed {{ formattedPinner }} and won’t see Pins they save anymore.',
                'Confirmation text after unfollowing a user',
              ),
              { formattedPinner },
            )
          : interpolateNamedTemplate(
              i18n._(
                'You unfollowed the board {{ formattedBoard }} and won’t see Pins saved to it anymore.',
                'Confirmation text after unfollowing a board',
              ),
              { formattedBoard },
            );

        return {
          action: 'unfollowBoard',
          actionOptions: {
            board_id: boardId,
          },
          title: '',
          subTitle,
        };
      }
    }
    return null;
  },
});
